export default function HowItWorks() {
  const steps = [
    {
      number: '01',
      title: 'Systems Discovery',
      description: 'We begin by mapping your operational workflows, identifying repetitive tasks, bottlenecks, and data dependencies across your organization.',
      icon: 'ri-search-line'
    },
    {
      number: '02',
      title: 'Architecture Design',
      description: 'Our team designs a structured automation blueprint integrating your CRM, communication tools, reporting systems, and operational software.',
      icon: 'ri-draft-line'
    },
    {
      number: '03',
      title: 'Intelligent Automation',
      description: 'Graystone builds automated workflows that eliminate manual tasks, streamline communication, and improve data consistency.',
      icon: 'ri-cpu-line'
    },
    {
      number: '04',
      title: 'Continuous Orchestration',
      description: 'Our internal orchestration layer, Grayson, monitors the system and ensures workflows continue operating smoothly.',
      icon: 'ri-loop-right-line'
    }
  ];

  return (
    <section className="relative bg-[#1A1C1E] py-32 overflow-hidden">
      {/* Blueprint grid texture */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(#00E5FF 1px, transparent 1px),
            linear-gradient(90deg, #00E5FF 1px, transparent 1px),
            linear-gradient(#00E5FF 1px, transparent 1px),
            linear-gradient(90deg, #00E5FF 1px, transparent 1px)
          `,
          backgroundSize: '100px 100px, 100px 100px, 20px 20px, 20px 20px',
          backgroundPosition: '-1px -1px, -1px -1px, -1px -1px, -1px -1px'
        }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-4xl lg:text-5xl font-bold text-[#F4F7F6] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            How Intelligent Architecture Is Built
          </h2>
          <p className="text-xl text-[#7F8C8D] leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
            Our automation systems follow a structured architectural process designed to eliminate operational friction and deliver reliable automation.
          </p>
        </div>

        {/* Process Steps */}
        <div className="relative">
          {/* Connector Line */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/40 to-transparent"></div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {steps.map((step, index) => (
              <div key={index} className="relative group">
                {/* Connector dots */}
                <div className="hidden lg:flex absolute top-24 left-1/2 -translate-x-1/2 w-4 h-4 items-center justify-center z-10">
                  <div className="w-3 h-3 rounded-full bg-[#00E5FF] group-hover:scale-150 transition-transform duration-300"></div>
                </div>

                <div className="relative bg-[#1A1C1E] border border-[#7F8C8D]/20 rounded-lg p-8 hover:border-[#00E5FF]/50 transition-all duration-300 h-full group-hover:shadow-lg group-hover:shadow-[#00E5FF]/10">
                  {/* Step Number */}
                  <div className="text-6xl font-bold text-[#00E5FF]/20 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {step.number}
                  </div>

                  {/* Icon */}
                  <div className="w-12 h-12 flex items-center justify-center mb-6">
                    <i className={`${step.icon} text-4xl text-[#00E5FF]`}></i>
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-[#F4F7F6] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {step.title}
                  </h3>
                  <p className="text-[#7F8C8D] leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Grayson Quote */}
        <div className="mt-20 text-center">
          <div className="inline-block relative">
            <div className="absolute inset-0 bg-[#00E5FF]/10 blur-xl"></div>
            <p className="relative text-xl lg:text-2xl text-[#00E5FF] font-light italic px-8 py-4" style={{ fontFamily: 'Inter, sans-serif' }}>
              Systems are nominal and workflows are fluid. — Grayson
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}